import { ImpactActions } from '../actions/impacts';
import { ImpactConfigActions } from '../actions/config';
import { ClickTypeFilter, ImpactTypeFilter, ImpactTypes } from '../types';
import { combineReducers } from '@fullstory/redux-util/utils/combineReducers';
import {
  BadClickType,
  CustomEvent,
  ImpactType,
  PageView,
  SeenEvent,
} from '@fullstory/proto/fs/services/alexa';
import { CustomQueryProto } from '@fullstory/proto/fs/services/webber/internal/impacts';
import { DateRangeID } from '@fullstory/client-api';
import { int64, Timestamp } from '@fullstory/proto/google/protobuf';

export const defaultClickFilter: ClickTypeFilter = {
  [BadClickType.CLICK_TYPE_RAGE]: true,
  [BadClickType.CLICK_TYPE_ERROR]: true,
  [BadClickType.CLICK_TYPE_DEAD]: true,
};

// web vital defaults are modified downstream via flag
export const defaultImpactFilter: ImpactTypeFilter = {
  [ImpactType.IMPACT_TYPE_PERF_EVENT]: true,
  [ImpactType.IMPACT_TYPE_CUSTOM_QUERY]: true,
  [ImpactType.IMPACT_TYPE_SEEN_EVENT]: true,
  [ImpactType.IMPACT_TYPE_CUSTOM_EVENT]: true,
  [ImpactType.IMPACT_TYPE_DEFINED_EVENT]: true,
  [ImpactType.IMPACT_TYPE_AJAX_ERROR]: true,
  [ImpactType.IMPACT_TYPE_CORE_WEB_VITALS]: false,
  [ImpactType.IMPACT_TYPE_PAGE_VIEW]: true,
  [ImpactType.IMPACT_TYPE_FID]: false,
  [ImpactType.IMPACT_TYPE_LCP]: false,
  [ImpactType.IMPACT_TYPE_CLS]: false,
  [ImpactType.IMPACT_TYPE_INP]: false,
  [ImpactType.IMPACT_TYPE_TTI]: false,
  [ImpactType.IMPACT_TYPE_TBT]: false,
  [ImpactType.IMPACT_TYPE_TTFB]: false,
};

/** Currently selected config */
export function currentConfigId(
  state: int64 | null = null,
  action: ImpactActions | ImpactConfigActions,
): int64 | null {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
    case ImpactTypes.ConfigUpdateSuccess:
    case ImpactTypes.ConfigCreateSuccess:
      return action.config.id;
    case ImpactTypes.ConfigDeleteSuccess:
    case ImpactTypes.ConfigNew:
      return null;
  }
  return state;
}

/** The segment currently shown in the Impacts table. */
function segmentId(
  state: string = 'everyone',
  action: ImpactActions | ImpactConfigActions,
): string {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.segmentId;
    case ImpactTypes.ConfigNew:
      return 'everyone';
  }
  return state;
}

/** Overridden date range with explicit start date */
function startDate(
  state: Date | null = null,
  action: ImpactActions | ImpactConfigActions,
): Date | null {
  switch (action.type) {
    case ImpactTypes.ConfigNew:
      return null;
    case ImpactTypes.ConfigLoad:
      return Timestamp.toDate(action.config.startTime);
  }
  return state;
}

/** Overridden date range with explicit end date */
function endDate(
  state: Date | null = null,
  action: ImpactActions | ImpactConfigActions,
): Date | null {
  switch (action.type) {
    case ImpactTypes.ConfigNew:
      return null;
    case ImpactTypes.ConfigLoad:
      return Timestamp.toDate(action.config.endTime);
  }
  return state;
}

/** Overridden date range with preset relative time */
function cannedDateRangeId(
  state: DateRangeID = DateRangeID.PastWeek,
  action: ImpactActions | ImpactConfigActions,
): DateRangeID {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.dateRangeId as DateRangeID;
    case ImpactTypes.ConfigNew:
      return DateRangeID.PastWeek;
  }
  return state;
}

/** The conversion currently shown in the Impacts table. */
function funnelId(
  state: string | null = null,
  action: ImpactActions | ImpactConfigActions,
): string | null {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.funnelId;
    case ImpactTypes.ConfigNew:
      return null;
  }
  return state;
}

function funnelIsUnnamed(
  state: boolean = false,
  action: ImpactActions | ImpactConfigActions,
): boolean {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.unnamed ?? false;
    case ImpactTypes.ConfigNew:
      return false;
  }
  return state;
}

/** The currently configured custom events. */
function customEvents(
  state: CustomEvent[] = [],
  action: ImpactActions | ImpactConfigActions,
): CustomEvent[] {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.customEvents ? action.config.customEvents : [];
    case ImpactTypes.ConfigNew:
      return [];
  }
  return state;
}

function pageviewEvents(
  state: PageView[] = [],
  action: ImpactActions | ImpactConfigActions,
): PageView[] {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.pageviewEvents ? action.config.pageviewEvents : [];
    case ImpactTypes.ConfigNew:
      return [];
  }
  return state;
}

/** The currently configured custom queries. */
function customQueries(
  state: CustomQueryProto[] = [],
  action: ImpactActions | ImpactConfigActions,
): CustomQueryProto[] {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.customQueries ? action.config.customQueries : [];
    case ImpactTypes.ConfigNew:
      return [];
  }
  return state;
}

/** The currently configured watched elements. */
function watchedElements(
  state: SeenEvent[] = [],
  action: ImpactActions | ImpactConfigActions,
): SeenEvent[] {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      if (action.config.watchedElements) {
        return action.config.watchedElements.map(d => ({ ...d }));
      } else {
        return [];
      }
    case ImpactTypes.ConfigNew:
      return [];
  }
  return state;
}

/** The set of click type filters that will be applied to `impacts` */
function clickTypeFilter(
  state: ClickTypeFilter = defaultClickFilter,
  action: ImpactActions | ImpactConfigActions,
): ClickTypeFilter {
  switch (action.type) {
    case ImpactTypes.ConfigLoad: {
      const loaded: { [key: string]: boolean | undefined } = { ...defaultClickFilter };
      Object.keys(loaded).forEach(
        key => (loaded[key] = action.config.clickTypeFilters.includes(+key)),
      );
      return loaded;
    }
    case ImpactTypes.FilterRageClicks:
      return {
        ...state,
        [BadClickType.CLICK_TYPE_RAGE]: action.filtered,
      };
    case ImpactTypes.FilterErrorClicks:
      return {
        ...state,
        [BadClickType.CLICK_TYPE_ERROR]: action.filtered,
      };
    case ImpactTypes.FilterDeadClicks:
      return {
        ...state,
        [BadClickType.CLICK_TYPE_DEAD]: action.filtered,
      };
    case ImpactTypes.ConfigNew:
      return defaultClickFilter;
  }
  return state;
}

/** The set of impact type filters that will be applied to `impacts` */
function impactTypeFilter(
  state: ImpactTypeFilter = defaultImpactFilter,
  action: ImpactActions | ImpactConfigActions,
): ImpactTypeFilter {
  switch (action.type) {
    case ImpactTypes.ConfigLoad: {
      const loaded: { [key: string]: boolean | undefined } = { ...defaultImpactFilter };
      Object.keys(loaded).forEach(
        key => (loaded[key] = action.config.impactTypeFilters.includes(+key)),
      );
      // if legacy web vital option is set, we need to set the new web vitals to the same value
      if (loaded[ImpactType.IMPACT_TYPE_CORE_WEB_VITALS] && action.splitWebVitals) {
        loaded[ImpactType.IMPACT_TYPE_FID] = true;
        loaded[ImpactType.IMPACT_TYPE_LCP] = true;
        loaded[ImpactType.IMPACT_TYPE_CLS] = true;
        loaded[ImpactType.IMPACT_TYPE_TBT] = true;
        loaded[ImpactType.IMPACT_TYPE_INP] = true;
        loaded[ImpactType.IMPACT_TYPE_TTI] = true;
        loaded[ImpactType.IMPACT_TYPE_TTFB] = true;
      }
      return loaded;
    }
    case ImpactTypes.FilterPagePerformance:
      return {
        ...state,
        [ImpactType.IMPACT_TYPE_PERF_EVENT]: action.filtered,
      };
    case ImpactTypes.FilterAjaxErrors:
      return {
        ...state,
        [ImpactType.IMPACT_TYPE_AJAX_ERROR]: action.filtered,
      };
    case ImpactTypes.ConfigNew:
      return {
        ...defaultImpactFilter,
        [ImpactType.IMPACT_TYPE_CORE_WEB_VITALS]: !action.splitWebVitals,
        [ImpactType.IMPACT_TYPE_FID]: action.splitWebVitals,
        [ImpactType.IMPACT_TYPE_LCP]: action.splitWebVitals,
        [ImpactType.IMPACT_TYPE_CLS]: action.splitWebVitals,
        [ImpactType.IMPACT_TYPE_TTFB]: action.splitWebVitals,
        [ImpactType.IMPACT_TYPE_INP]: action.splitWebVitals,
        [ImpactType.IMPACT_TYPE_TTI]: false, // always default to false
        [ImpactType.IMPACT_TYPE_TBT]: false, // always default to false
      };
  }
  return state;
}

/** The currently configured defined events. */
function definedEventIds(
  state: string[] = [],
  action: ImpactActions | ImpactConfigActions,
): string[] {
  switch (action.type) {
    case ImpactTypes.ConfigLoad:
      return action.config.definedEventIds ? action.config.definedEventIds : [];
    case ImpactTypes.ConfigNew:
      return [];
  }
  return state;
}

// temporary state for splitting web vitals (used for counting filters ie `numCannedFilters` and the like)
function isSplittingWebVitals(state: boolean = false, action: ImpactConfigActions): boolean {
  switch (action.type) {
    case ImpactTypes.ConfigNew:
    case ImpactTypes.ConfigLoad:
      return action.splitWebVitals;
  }
  return state;
}

type CurrentConfigState = {
  funnelId: ReturnType<typeof funnelId>;
  segmentId: ReturnType<typeof segmentId>;
  cannedDateRangeId: ReturnType<typeof cannedDateRangeId>;
  startDate: ReturnType<typeof startDate>;
  endDate: ReturnType<typeof endDate>;
  customEvents: ReturnType<typeof customEvents>;
  pageviewEvents: ReturnType<typeof pageviewEvents>;
  watchedElements: ReturnType<typeof watchedElements>;
  customQueries: ReturnType<typeof customQueries>;
  clickTypeFilter: ReturnType<typeof clickTypeFilter>;
  impactTypeFilter: ReturnType<typeof impactTypeFilter>;
  definedEventIds: ReturnType<typeof definedEventIds>;
  unnamed: ReturnType<typeof funnelIsUnnamed>;
  isSplittingWebVitals: ReturnType<typeof isSplittingWebVitals>;
};

// TODO(johnverrone): once the ExpConversionsLandingPage flag is flipped on for everyone this could
// just be a single reducer that loads the value of pendingConfig into currentConfig on 'Analyze'
export const currentConfig = combineReducers<CurrentConfigState>({
  funnelId,
  segmentId,
  cannedDateRangeId,
  startDate,
  endDate,
  customEvents,
  pageviewEvents,
  watchedElements,
  customQueries,
  clickTypeFilter,
  impactTypeFilter,
  definedEventIds,
  unnamed: funnelIsUnnamed,
  isSplittingWebVitals,
});
